/* 同步引擎（客戶端）。三個原則：
   1. 本機優先：任何改動先落地本機並進待推送佇列，同步失敗不影響使用。
   2. 絕不靜默丟字：偵測到衝突就保留雙方，遠端版本存成一條衝突副本。
   3. 冪等：同一筆 op 送兩次也只會存一次（伺服器以 opId 去重）。 */
import type { Category, Concept, KnowledgeLink, SyncOp } from "../domain/types.ts";
import { applyOp, beats, conflictingOps, realFields } from "../domain/oplog.ts";
import type { Repository } from "../data/repository.ts";
import type { KeyRecord } from "../crypto/keys.ts";
import { hkdfKey, openJson, sealJson } from "../crypto/box.ts";
import type { Sealed } from "../crypto/box.ts";
import type { OpPayload, PullResponse, PushResponse, WireOp } from "./protocol.ts";
import { fromPayload, toPayload } from "./protocol.ts";

export interface SyncTransport {
  putKeyRecord(keyId: string, record: KeyRecord): Promise<void>;
  getKeyRecord(keyId: string): Promise<KeyRecord | null>;
  push(keyId: string, authKey: string, deviceId: string, ops: WireOp[]): Promise<PushResponse>;
  pull(keyId: string, authKey: string, since: number, limit?: number): Promise<PullResponse>;
}

export class SyncHttpError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "SyncHttpError";
    this.status = status;
  }
}

export class HttpTransport implements SyncTransport {
  private base: string;
  constructor(baseUrl: string) { this.base = baseUrl.replace(/\/+$/, ""); }

  private async call<T>(path: string, init: RequestInit & { auth?: [string, string] } = {}): Promise<T> {
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (init.auth) { headers["x-lifesage-key"] = init.auth[0]; headers["x-lifesage-auth"] = init.auth[1]; }
    const res = await fetch(this.base + path, { ...init, headers: { ...headers, ...(init.headers ?? {}) } });
    const text = await res.text();
    const body = text ? JSON.parse(text) : null;
    if (!res.ok) throw new SyncHttpError(res.status, (body && body.error) || ("HTTP " + res.status));
    return body as T;
  }

  async putKeyRecord(keyId: string, record: KeyRecord): Promise<void> {
    await this.call("/v1/keys", { method: "POST", body: JSON.stringify({ keyId, record }) });
  }
  async getKeyRecord(keyId: string): Promise<KeyRecord | null> {
    try {
      const out = await this.call<{ record: KeyRecord }>("/v1/keys?key=" + encodeURIComponent(keyId));
      return out.record;
    } catch (e) {
      if (e instanceof SyncHttpError && e.status === 404) return null;
      throw e;
    }
  }
  push(keyId: string, authKey: string, deviceId: string, ops: WireOp[]): Promise<PushResponse> {
    return this.call<PushResponse>("/v1/ops", {
      method: "POST", auth: [keyId, authKey], body: JSON.stringify({ deviceId, ops }) });
  }
  pull(keyId: string, authKey: string, since: number, limit = 200): Promise<PullResponse> {
    return this.call<PullResponse>("/v1/ops?since=" + since + "&limit=" + limit, { auth: [keyId, authKey] });
  }
}

export interface SyncOutcome {
  pushed: number;
  applied: number;
  conflicts: number;
  skipped: number;
  cursor: number;
  error?: string;
}

export class SyncEngine {
  private repo: Repository;
  private transport: SyncTransport;
  private keys: { keyId: string; main: CryptoKey; adult?: CryptoKey };
  private authKey: string;
  private deviceId: string;

  constructor(opts: {
    repo: Repository; transport: SyncTransport;
    keys: { keyId: string; main: CryptoKey; adult?: CryptoKey }; authKey: string;
    deviceId: string;
  }) {
    this.repo = opts.repo;
    this.transport = opts.transport;
    this.keys = opts.keys;
    this.authKey = opts.authKey;
    this.deviceId = opts.deviceId;
  }

  /** 每一筆 op 用自己的金鑰（由 opId 派生）—— 一筆外洩不波及他筆，而且不需要共用金鑰。 */
  private async opKey(base: CryptoKey, opId: string): Promise<CryptoKey> {
    return hkdfKey(base, "lifesage/op/v1/" + opId);
  }

  /** 這一筆 op 屬於成人區嗎？（決定用哪把金鑰加密）
      注意：**伺服器不會知道答案** —— 線上傳的都只是密文，形狀完全相同。 */
  private async isAdultOp(op: SyncOp): Promise<boolean> {
    if (op.entityType === "concept") {
      const to = (op.changedFields["nsfw"] as { to?: unknown } | undefined)?.to;
      if (to === true) return true;
      if (to === false) return false;
      return (await this.repo.getConcept(op.entityId))?.nsfw === true;
    }
    if (op.entityType === "link") {
      const link = await this.repo.getLink(op.entityId);
      if (!link) return false;
      const [from, to] = await Promise.all([
        this.repo.getConcept(link.fromId), this.repo.getConcept(link.toId)]);
      return !!(from?.nsfw || to?.nsfw);
    }
    return false;
  }

  async sealOp(op: SyncOp): Promise<WireOp> {
    const base = (await this.isAdultOp(op)) && this.keys.adult ? this.keys.adult : this.keys.main;
    const blob: Sealed = await sealJson(await this.opKey(base, op.opId), toPayload(op));
    return { opId: op.opId, deviceId: this.deviceId, at: op.at, serverSeq: 0, blob };
  }

  /** 開的時候不知道是哪一把，所以兩把都試 —— GCM 認證失敗就換下一把。
      代價是成人 op 多一次解密嘗試（可忽略）；換到的是伺服器看不出任何差別。 */
  async openOp(wire: WireOp): Promise<SyncOp> {
    const candidates = this.keys.adult ? [this.keys.main, this.keys.adult] : [this.keys.main];
    for (const base of candidates) {
      try {
        const payload = await openJson<OpPayload>(await this.opKey(base, wire.opId), wire.blob);
        return fromPayload(wire.opId, wire.deviceId, wire.at, payload);
      } catch { /* 換下一把 */ }
    }
    throw new Error("這筆變更解不開：金鑰不符或內容損毀。");
  }

  async push(): Promise<number> {
    const pending = await this.repo.outbox();
    if (!pending.length) return 0;
    const wire: WireOp[] = [];
    for (const op of pending) wire.push(await this.sealOp(op));
    const res = await this.transport.push(this.keys.keyId, this.authKey, this.deviceId, wire);
    if (res.accepted > 0 || res.duplicates > 0) {
      const done = wire.slice(0, res.accepted + res.duplicates).map((w) => w.opId);
      await this.repo.clearOutbox(done);
    }
    return res.accepted;
  }

  private async localEntity(op: SyncOp): Promise<Concept | KnowledgeLink | Category | null> {
    if (op.entityType === "concept") return this.repo.getConcept(op.entityId);
    if (op.entityType === "link") return this.repo.getLink(op.entityId);
    if (op.entityType === "category") return this.repo.getCategory(op.entityId);
    return null;
  }

  private async putEntity(
    type: SyncOp["entityType"], entity: Concept | KnowledgeLink | Category,
  ): Promise<void> {
    if (type === "concept") await this.repo.upsertConcept(entity as Concept);
    else if (type === "category") await this.repo.upsertCategory(entity as Category);
    else await this.repo.upsertLink(entity as KnowledgeLink);
  }

  /** 套用一筆遠端 op。回傳它被當成新變更、衝突、還是重複。

      三種實體的衝突處理**刻意不同**：
      - 概念：敗方的內容另存成一條副本（副本 id 由敗方 op 決定，兩端會算出同一個）。
        這是「絕不靜默丟字」的落實 —— 你寫的句子一定留得住。
      - 連結：直接由勝方決定，不做副本。因為「同一對知識之間的第二條棉線」沒有意義，
        而連結的 note 只是短標籤，不是你在這裡寫的正文。
      - 類別：同樣不做副本。抽屜名字沒有「不能丟的字」，而且憑空長出一個同名抽屜
        比少一個字更難收拾；兩端仍照同一條確定性規則收斂。 */
  async applyRemote(op: SyncOp): Promise<"applied" | "conflict" | "skipped"> {
    if (op.entityType !== "concept" && op.entityType !== "link" && op.entityType !== "category") {
      return "skipped";
    }

    const existing = await this.repo.opsForEntity(op.entityId);
    if (existing.some((o) => o.opId === op.opId)) return "skipped";

    const local = await this.localEntity(op);
    /* 建立型 op 依 entity id 去重：兩端各自算出同一個 id 的情況（衝突副本）不會變成兩條。 */
    if (op.baseRev === 0 && local && existing.length > 0) return "skipped";

    if (!local) {
      /* 本機沒有這個實體：從歷史把它重建出來。建立型 op 帶著全部欄位。 */
      const chain = [...existing, op].sort((a, b) => a.baseRev - b.baseRev || a.at - b.at);
      let rebuilt: Record<string, unknown> | null = null;
      for (const o of chain) rebuilt = applyOp((rebuilt ?? { rev: 0 }) as { rev: number }, o) as unknown as Record<string, unknown>;
      if (rebuilt) {
        await this.putEntity(op.entityType, rebuilt as unknown as Concept);
        await this.repo.recordRemoteOp(op);
        return "applied";
      }
      return "skipped";
    }

    const conflicting = conflictingOps(op, existing);
    if (conflicting.length) {
      const localBest = conflicting.reduce((a, b) => (beats(a, b) ? a : b));
      const incomingWins = beats(op, localBest);

      if (op.entityType !== "concept") {
        if (incomingWins) await this.putEntity(op.entityType, applyOp(local as KnowledgeLink, op));
        await this.repo.recordRemoteOp(op);
        return "conflict";
      }

      /* 概念的確定性勝負：敗方的內容另存成一條副本，原知識套用勝方的值，兩端收斂。 */
      const loser = incomingWins ? localBest : op;
      const fields = [...new Set([...realFields(op), ...conflicting.flatMap(realFields)])];
      const loserValues: Record<string, unknown> = {};
      for (const f of fields) {
        loserValues[f] = incomingWins
          ? (local as unknown as Record<string, unknown>)[f]
          : (op.changedFields[f] as { to: unknown } | undefined)?.to;
      }
      /* 副本用 op 管線建立：它才會有一筆 op，也才會同步出去。 */
      await this.repo.saveConcept(null, {
        ...(loserValues as { summary?: string }), id: "cfl-" + loser.opId,
        conflictOf: op.entityId, status: "draft",
      } as never, "conflict-resolve");
      await this.repo.recordRemoteOp(op);
      if (incomingWins) await this.repo.upsertConcept(applyOp(local as Concept, op));
      return "conflict";
    }

    await this.putEntity(op.entityType, applyOp(local as Concept, op));
    await this.repo.recordRemoteOp(op);
    return "applied";
  }

  async pull(): Promise<{ applied: number; conflicts: number; skipped: number; cursor: number }> {
    let cursor = await this.repo.getCursor();
    let applied = 0, conflicts = 0, skipped = 0;
    for (;;) {
      const res = await this.transport.pull(this.keys.keyId, this.authKey, cursor);
      for (const wire of res.ops) {
        if (wire.deviceId === this.deviceId) { cursor = Math.max(cursor, wire.serverSeq); continue; }
        const op = await this.openOp(wire);
        const verdict = await this.applyRemote(op);
        if (verdict === "applied") applied++;
        else if (verdict === "conflict") conflicts++;
        else skipped++;
        cursor = Math.max(cursor, wire.serverSeq);
      }
      if (res.lastSeq > cursor) cursor = res.lastSeq;
      await this.repo.setCursor(cursor);
      if (!res.more) break;
    }
    return { applied, conflicts, skipped, cursor };
  }

  /** 一次完整的同步：推到沒東西可推為止。
      為什麼要有界迴圈：套用遠端變更可能**產生新的本機 op** ——
      最典型的是衝突副本（它本身也要同步出去）。只做一輪 push→pull 會留下一筆永遠等下次的待推送，
      使用者在「立即同步」之後仍看到「待推送 1」會以為壞了。 */
  async runOnce(): Promise<SyncOutcome> {
    try {
      let pushed = 0, applied = 0, conflicts = 0, skipped = 0, cursor = await this.repo.getCursor();
      for (let round = 0; round < 4; round++) {
        pushed += await this.push();
        const pulled = await this.pull();
        applied += pulled.applied;
        conflicts += pulled.conflicts;
        skipped += pulled.skipped;
        cursor = pulled.cursor;
        if ((await this.repo.outbox()).length === 0) break;
      }
      return { pushed, applied, conflicts, skipped, cursor };
    } catch (e) {
      return { pushed: 0, applied: 0, conflicts: 0, skipped: 0,
        cursor: await this.repo.getCursor(), error: e instanceof Error ? e.message : String(e) };
    }
  }
}

/* authKey 的派生統一住在 crypto/keys.ts（authKeyFromBase）。
   這裡不再自己寫一份 —— 兩份實作遲早會分歧，而分歧的那一天就是救援碼失效的那一天。 */